/**
 * Logarithmic market-cap slider transforms used by ControlPanel.
 * Pure math, no React.
 *
 * The slider is a linear 0–100 control over a log-scale dollar range:
 *   pos = 0   → MIN_CAP ($5B)
 *   pos = 100 → MAX_CAP ($10T)
 * Log-scale because $5B → $50B feels like the same "step" as $50B → $500B.
 *
 * Bumped 5T → 10T on 2026-04-29 — NVDA hit $5.26T, the largest stock in
 * the universe. Future-proofed for the next mega-cap.
 */

export const MIN_CAP = 5e9
export const MAX_CAP = 1e13

export function capToSlider(cap: number): number {
  if (Number.isNaN(cap)) return 0
  if (cap >= MAX_CAP) return 100  // includes +Infinity
  if (cap <= MIN_CAP) return 0    // includes -Infinity and zero/negative
  return Math.round(
    ((Math.log(cap) - Math.log(MIN_CAP)) / (Math.log(MAX_CAP) - Math.log(MIN_CAP))) * 100,
  )
}

export function sliderToCap(pos: number): number {
  if (pos <= 0) return MIN_CAP
  if (pos >= 100) return MAX_CAP
  return Math.exp(
    Math.log(MIN_CAP) + (pos / 100) * (Math.log(MAX_CAP) - Math.log(MIN_CAP)),
  )
}

/** Format a market cap as a human-readable string ($5B / $500M). */
export function fmtCap(v: number): string {
  if (v >= 1e12) return `$${(v / 1e12).toFixed(1)}T`
  if (v >= 1e9) return `$${(v / 1e9).toFixed(0)}B`
  return `$${(v / 1e6).toFixed(0)}M`
}
